import { cn } from '@/lib/utils';
import { GutterClock } from '@/components/blocks/gutter-clock';

/**
 * Calendar skeletons (Antonin, 13 Oct: "calendar in practice needs proper skeleton loading because the practitioner
 * takes a long time to arrive. The rest of the content too. Make sure nothing jumps too much once it's loaded").
 *
 * Each skeleton draws the SAME frame as the loaded view (gutter, headers, hour rows at the real heights, chip and card
 * boxes at their real sizes), so only the grey bars give way to content: nothing above or below moves when data lands.
 * The bars are grey-200 and pulse; under reduced motion they stay still.
 */
export const SKELETON = 'animate-pulse motion-reduce:animate-none rounded-md bg-grey-200';

export function SkeletonBar({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <span aria-hidden className={cn('block', SKELETON, className)} style={style} />;
}

/** Deterministic "booked" pattern: the same blocks on every render, spread so the grid reads as a day. */
export const SKELETON_BLOCKS: ReadonlyArray<{ col: number; at: number; len: number }> = [
  { col: 0, at: 0.25, len: 0.75 }, { col: 0, at: 2, len: 1 }, { col: 0, at: 4.5, len: 0.5 },
  { col: 1, at: 1, len: 0.5 }, { col: 1, at: 3, len: 1.5 },
  { col: 2, at: 0.5, len: 1 }, { col: 2, at: 2.5, len: 0.5 }, { col: 2, at: 5, len: 1 },
  { col: 3, at: 1.5, len: 1 }, { col: 3, at: 4, len: 0.75 },
  { col: 4, at: 0, len: 0.5 }, { col: 4, at: 2, len: 1 },
  { col: 5, at: 3, len: 0.5 },
  { col: 6, at: 1, len: 0.75 },
];

/** A skeleton event block inside a positioned column (4 px off the hour line, like the real cards). */
export function SkeletonEvent({ top, height }: { top: number; height: number }) {
  return (
    <div
      aria-hidden
      className="absolute left-1 right-1 flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-card pl-3 pr-2 py-1.5"
      style={{ top: top + 4, height: Math.max(height - 8, 20) }}
    >
      <SkeletonBar className="h-3 w-3/4" />
      {height >= 40 && <SkeletonBar className="h-2 w-1/2" />}
    </div>
  );
}

/**
 * The column views (Doctor, Room) while their columns are unknown: the same header strip, gutter and hour rows as
 * the loaded grid, four columns, a few blocks.
 */
export function ColumnsCalendarSkeleton({
  label,
  hours,
  hourHeight,
  columns = 4,
  minColumnWidth = 160,
  avatar = true,
}: {
  label: string;
  hours: readonly number[];
  hourHeight: number;
  columns?: number;
  minColumnWidth?: number;
  /** Doctor columns open on a photo; room columns on a name only. */
  avatar?: boolean;
}) {
  const cols = Array.from({ length: columns }, (_, i) => i);
  return (
    <div className="flex flex-col flex-1 min-h-0 bg-surface-card" aria-busy="true" aria-label={`${label}, loading`}>
      <div className="px-4 py-3 border-b border-border-default">
        <p className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">{label}</p>
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="flex border-b border-border-default bg-surface-card">
          <GutterClock className="w-16 flex-shrink-0 border-r border-border-subtle px-2 py-2" />
          {cols.map((c) => (
            <div key={c} className="flex flex-1 items-center gap-2 border-r border-border-subtle last:border-r-0 px-3 py-2" style={{ minWidth: minColumnWidth }}>
              {avatar && <SkeletonBar className="h-8 w-8 shrink-0 rounded-full" />}
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <SkeletonBar className="h-3 w-3/4" />
                <SkeletonBar className="h-2 w-1/2" />
              </div>
            </div>
          ))}
        </div>
        <div className="relative">
          {hours.map((hour) => (
            <div key={hour} className="flex border-b border-border-subtle" style={{ height: hourHeight }}>
              <div className="w-16 flex-shrink-0 px-2 pt-1 border-r border-border-subtle">
                <SkeletonBar className="h-3 w-10" />
              </div>
              {cols.map((c) => (
                <div key={c} className="flex-1 border-r border-border-subtle last:border-r-0" style={{ minWidth: minColumnWidth }} />
              ))}
            </div>
          ))}
          <div className="absolute inset-0 flex pointer-events-none">
            <div className="w-16 flex-shrink-0" />
            {cols.map((c) => (
              <div key={c} className="relative flex-1" style={{ minWidth: minColumnWidth }}>
                {SKELETON_BLOCKS.filter((b) => b.col === c && b.at + b.len <= hours.length).map((b, i) => (
                  <SkeletonEvent key={i} top={b.at * hourHeight} height={b.len * hourHeight} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
